import type { BlobInfo, ICaseDataRepository } from '../CaseDataProvider';
import type { CaseFilesApi, NewPackageFileCreateInfo, NewPackageInfo } from '../CaseFilesApi';
import type { CaseInfo, CaseInfoApi } from '../CaseInfoApi';
import type { PackageInfoStore } from './PackageInfoStore';
import { CaseContext } from './CaseContext';

export class NetworkCaseDataRepository implements ICaseDataRepository {
  private _caseInfoApi: CaseInfoApi;

  private _caseFilesApi: CaseFilesApi;

  private _packageInfoStore: PackageInfoStore;

  private readonly _minExpirationTimeMs = 60 * 1000; // (1 minute) [chebotarev] Time left for the link expiration. If less remained - will cause new link retrieval.

  private _caseContext?: CaseContext;

  public constructor(caseInfoApi: CaseInfoApi, caseFilesApi: CaseFilesApi, packageInfoStore: PackageInfoStore) {
    this._caseInfoApi = caseInfoApi;
    this._caseFilesApi = caseFilesApi;
    this._packageInfoStore = packageInfoStore;
  }

  public setContext(caseId: string, packageId: string): void {
    this._caseContext = new CaseContext({ caseId, packageId });
  }

  public uploadBlob(uploadUrl: string, blob: Blob): Promise<Response> {
    return fetch(uploadUrl, { method: 'PUT', body: blob });
  }

  public derivePackage(files: Array<NewPackageFileCreateInfo>): Promise<NewPackageInfo> {
    if (!this._caseContext) {
      return Promise.reject(new Error('Case context is not set'));
    }
    return this._caseFilesApi.derivePackage(this._caseContext?.packageId, files);
  }

  public saveDerivedPackage(newPackageId: string): Promise<void> {
    if (!this._caseContext) {
      return Promise.reject(new Error('Case context is not set'));
    }
    return this._caseFilesApi.saveDerivedPackage(this._caseContext.caseId, newPackageId, this._caseContext.packageId);
  }

  public approveDerivedPackage(newPackageId: string): Promise<string> {
    if (!this._caseContext) {
      return Promise.reject(new Error('Case context is not set'));
    }
    return this._caseFilesApi.approveDerivedPackage(
      this._caseContext.caseId,
      newPackageId,
      this._caseContext.packageId
    );
  }

  public rejectDerivedPackage(newPackageId: string): Promise<string> {
    if (!this._caseContext) {
      return Promise.reject(new Error('Case context is not set'));
    }
    return this._caseFilesApi.rejectDerivedPackage(this._caseContext.caseId, newPackageId, this._caseContext.packageId);
  }

  public getCaseInfo(): Promise<CaseInfo> {
    if (!this._caseContext) {
      return Promise.reject(new Error('Case context is not set'));
    }
    return this._caseInfoApi.getCaseInfo(this._caseContext.caseId, this._caseContext.packageId);
  }

  public async getCaseFilesList(): Promise<Array<BlobInfo>> {
    if (!this._caseContext) {
      return Promise.reject(new Error('Case context is not set'));
    }
    const filesInfo = await this._caseFilesApi.getCaseFilesList(this._caseContext.packageId);
    this._cacheFilesListInfo(filesInfo);
    return filesInfo;
  }

  public async getCaseFileInfo(fileDescriptor: string): Promise<BlobInfo> {
    if (!this._caseContext) {
      return Promise.reject(new Error('Case context is not set'));
    }
    const fileInfo = await this._caseFilesApi.getCaseFileInfo(this._caseContext?.packageId, fileDescriptor);
    const availableInfo = this._packageInfoStore.getBlobInfo(fileDescriptor);
    if (availableInfo && availableInfo.md5 !== fileInfo.md5) {
      console.warn('MD5 mismatch for file descriptor: ' + fileDescriptor);
    }

    this._packageInfoStore.addBlobInfo(fileDescriptor, fileInfo);
    return fileInfo;
  }

  public async getCaseBlob(descriptor: string): Promise<ArrayBuffer> {
    const blobUrl = await this._resolveBlobUrl(descriptor);
    const fetchPromise = await fetch(blobUrl);
    const arrayBufferPromise = fetchPromise.arrayBuffer();
    this._packageInfoStore.addBlob(descriptor, arrayBufferPromise);
    return arrayBufferPromise;
  }

  public hasCaseBlobInfo(descriptor: string): Promise<boolean> {
    return Promise.resolve(this._packageInfoStore.getBlobInfo(descriptor) !== undefined);
  }

  public getCaseBlobUrlResolver(): (descriptor: string) => Promise<string> {
    return this._resolveBlobUrl.bind(this);
  }

  private _cacheFilesListInfo(fileInfos: Array<BlobInfo>): void {
    fileInfos.forEach((fileInfo) => {
      this._packageInfoStore.addBlobInfo(fileInfo.descriptor, fileInfo);
    });
  }

  private async _resolveBlobUrl(descriptor: string): Promise<string> {
    // [chebotarev]
    // WARNING!
    // STS urls are not intended for caching, as they have limited lifetime (1 hour as of moment of writing).
    // Never cache STS urls!
    // Always use getShortLivingCaseBlobUrlResolver() to get url in place.
    let blobInfo = this._packageInfoStore.getBlobInfo(descriptor);
    if (!blobInfo) {
      return Promise.reject(new Error('Blob info not found for descriptor: ' + descriptor));
    }

    if (blobInfo.expiration - Date.now() < this._minExpirationTimeMs) {
      // [chebotarev]
      // If link is about to expire - retrieve new one and update it in the cache
      // Originally all links are retrieved in single server API call.
      // Herein we are renewing links one-by-ne.
      // I think it's ok as for now.
      // But in case of performance issues in future, maybe we could refresh all the links together (if any of them is expired).
      blobInfo = await this.getCaseFileInfo(descriptor);

      return Promise.resolve(blobInfo.url);
    }

    return Promise.resolve(blobInfo.url);
  }
}
